import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import { Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 } from 'uuid';

import { CrossSvg, ExclamationMarkSvg } from '../../../assets/icons';
import { AlignStandardsSvg } from '../../../assets/icons/AlignStandardsSvg';
import CuripodAIStarsSvg from '../../../assets/icons/CuripodAIStarsSvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import FolderSvg from '../../../assets/icons/FolderSvg';
import GoogleDriveSvg from '../../../assets/icons/GoogleDriveSvg';
import UploadsSvg from '../../../assets/icons/UploadsSvg';
import theme from '../../../assets/theme';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useConsumeGenericGeneratorAsNewPresentation from '../../../hooks/useConsumeGenericGeneratorAsNewPresentation';
import {
  saveLocalDynamicLessonState,
  useDynamicLessonStateContext,
} from '../../../hooks/useCreateLessonState';
import useDebounce from '../../../hooks/useDebounce';
import useGoogleDrive from '../../../hooks/useGoogleDrive';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { useUploadGoogleDrive } from '../../../hooks/useUploadGoogleDrive';
import { useUploadPresentation } from '../../../hooks/useUploadPresentation';
import { Standards } from '../../../services/backendService/types';
import { supportedLanguages } from '../../../utils/createLessonPageUtils';
import { isIos } from '../../../utils/navigator';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { useGoogleDrivePicker } from '../../_atoms/GoogleDrivePicker';
import { CallbackDoc } from '../../_atoms/GoogleDrivePicker/typeDefs';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import ShowLessonCreationProgressBar from '../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import { FloatingSelect } from './components/FloatingSelect';
import { SubPageLayout } from './CreatePage';
import { StandardsPicker } from './StandardsPicker';
import { UploadedFile } from './types';

export const TopicInput = ({
  prompt,
  handleNewPrompt,
  placeholderText,
}: {
  prompt: string | undefined;
  handleNewPrompt: (prompt: string) => void;
  placeholderText?: string;
}) => {
  const isLargerThan600 = useBreakPoint(600);
  return (
    <>
      <TextArea
        rows={isLargerThan600 ? 3 : 6}
        textAreaStyled={{
          boxShadow: theme.effects.floatingCreatorElementShadow,
          border: 'none',
        }}
        style={{
          width: '100%',
        }}
        value={prompt}
        placeholder={
          placeholderText
            ? placeholderText
            : 'Add your learning objective, the topic, or content. Be explicit! \nE.g Engaging review with real-life examples of... Three practice questions for the skill... Make it 8 slides...'
        }
        onChange={e => handleNewPrompt(e.target.value)}
        maxLength={6000} //TODO how many characters should we allow?
      />
    </>
  );
};

export const CurifyTab = () => {
  const { t } = useTranslate();
  const { tenantId, router } = useRouterMatch();
  const isDesktop = useBreakPoint(745);
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const dynamicLessonState = useDynamicLessonStateContext(c => c.dynamicLessonState);
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const selectedStandards = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedStandards,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const [currentPrompt, setCurrentPrompt] = useState<string | undefined>(prompt);
  const debouncedPrompt = useDebounce(currentPrompt, 1000);
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const languages = useDynamicLessonStateContext(c => c.dynamicLessonState.languages);
  const subject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);

  useEffect(() => {
    if (debouncedPrompt !== undefined) {
      dispatchAndSync({
        type: 'ON_PROMPT_CHANGE',
        payload: currentPrompt,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPrompt]);

  const [isStandardsSelectorOpen, setIsStandardsSelectorOpen] = useState<{
    isOpen: boolean;
    canSwitchSubject?: boolean;
    subject?: { id: string; label: string };
  }>({ isOpen: false });
  const metrics = useMetrics();
  const [helpFillForm, setHelpFillForm] = useState(false);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const presetState = userAccessBadge?.organization?.region;
  const [showErrorModal, setShowErrorModal] = useState(false);
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  const isUSUser = userAccessBadge?.organization?.country === 'US';
  const {
    consumeGenerator,
    isLoading: isGeneratingLesson,
    isError,
    data,
    error,
  } = useConsumeGenericGeneratorAsNewPresentation(tenantId);

  const isLargerThan850 = useBreakPoint(850);

  const isGetLessonButtonDisabled = useMemo(() => {
    return !grade || !uploadedFile;
  }, [grade, uploadedFile]);

  const formErrorText = useMemo(() => {
    if (!grade) {
      return `Don't forget to choose a grade`;
    }
    if (!uploadedFile) {
      return `Don't forget to upload a presentation`;
    }
    return '';
  }, [grade, uploadedFile]);

  const constructPrompt = () => {
    if (selectedStandards.length > 0) {
      const standardsText = selectedStandards
        .map(s => `${s.label} ${s.description}`)
        .join('\n');
      return [currentPrompt, standardsText].join('\n\n');
    }
    return currentPrompt;
  };

  const handleStandardsSelected = (standards: Standards[]) => {
    setIsStandardsSelectorOpen({ isOpen: false, canSwitchSubject: false });
    dispatchAndSync({
      type: 'SET_STANDARDS',
      payload: standards,
    });
  };

  const removeStandard = (standardId: string) => {
    metrics.logEvent('CreatePage.standardRemoved');
    dispatchAndSync({
      type: 'REMOVE_STANDARD',
      payload: standardId,
    });
  };

  useEffect(() => {
    if (data) {
      metrics.logEvent('CreatePage.Generator.Consume.CurifyLessonSuccessRedirect', {
        generatorId: 'genericFullLesson',
        generatorName: 'genericFullLesson',
        standards: selectedStandards.map(s => s.id),
        subject: data.metadata?.subject,
        grade: grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        languages: languages,
        language: languages[0],
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: [currentPrompt || ''].join('\n\n'),
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  const handleGetLesson = () => {
    if (grade) {
      saveLocalDynamicLessonState({ grade, languages });

      const finalPromptData = {
        id: v4(),
        dynamicLessonState,
        createdAt: new Date(),
      };

      try {
        if (typeof window !== 'undefined') {
          const existingRecentPrompts = JSON.parse(
            localStorage.getItem('@curipod_v1_createWithAI_recentPrompts') || '[]',
          );
          localStorage.setItem(
            '@curipod_v1_createWithAI_recentPrompts',
            //max 50 recent prompts saved to local storage
            JSON.stringify([finalPromptData, ...existingRecentPrompts].slice(0, 50)),
          );
        }
      } catch (error) {
        console.error('Error saving recent prompts', error);
      }

      consumeGenerator({
        prompt: constructPrompt() || ' ',
        grade: grade,
        subject: subject,
        fileType: uploadedFile?.fileType,
        filePath: uploadedFile?.uploadedFileId,
        languages: [...languages],
      });
    }
  };

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  const handleFileUploaded = (uploadedFile: UploadedFile) => {
    dispatchAndSync({
      type: 'SET_UPLOADED_FILE',
      payload: uploadedFile,
    });
  };

  const showLoader = isGeneratingLesson || data;

  return (
    <>
      {!showLoader && (
        <SubPageLayout
          pageTitle={t('CurifyTab.PageTitle', 'Make my slides interactive with AI')}
        >
          <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
                <FoldedHandsSVG />
              </IconWrapper>

              <ErrorMessage>
                <>
                  <ErrorMessage style={{ fontWeight: 800 }}>
                    <T
                      keyName="CurifyTab.ErrorMessage.SomethingWentWrong"
                      defaultValue="Uh-oh! Something went wrong on our end"
                    />
                  </ErrorMessage>

                  <ErrorMessage style={{ fontSize: '16px' }}>
                    {error?.customMessage ||
                      'Please try again or contact support if the issue persists'}
                  </ErrorMessage>
                </>
              </ErrorMessage>
              <Button
                styles={{ marginTop: 20 }}
                width="120px"
                onClick={() => setShowErrorModal(false)}
              >
                <T keyName="Ok" defaultValue="Ok" />
              </Button>
            </div>
          </BaseModal>
          <FormContainer>
            {isStandardsSelectorOpen.isOpen && (
              <BaseModal
                visible
                noPadding
                onCancel={() =>
                  setIsStandardsSelectorOpen({ isOpen: false, canSwitchSubject: false })
                }
                width={isDesktop ? '80vw' : '95vw'}
                centered
              >
                <StandardsPicker
                  onStandardsSelected={handleStandardsSelected}
                  presetGrade={grade}
                  canSwitchSubject={isStandardsSelectorOpen.canSwitchSubject}
                  initialSelectedStandards={selectedStandards}
                />
              </BaseModal>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: isDesktop ? 'center' : 'start',
                gap: 30,
                flexWrap: 'wrap',
                flexDirection: isDesktop ? 'row' : 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: isDesktop ? 'center' : 'start',
                  gap: 10,
                  flexDirection: isDesktop ? 'row' : 'column',
                  width: isDesktop ? 'auto' : '100%',
                }}
              >
                <p style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>
                  <T
                    keyName="1. Add your presentation"
                    defaultValue="1. Add your presentation"
                  />
                </p>
                <div style={{ width: isDesktop ? '205px' : '100%' }}>
                  <UploadFileInput onFileUploaded={handleFileUploaded} />
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: isDesktop ? 'center' : 'start',
                  gap: 10,
                  flexWrap: 'wrap',
                  flexDirection: isDesktop ? 'row' : 'column',
                  width: isDesktop ? 'auto' : '100%',
                }}
              >
                <p style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>
                  <T keyName="2. Choose your grade" defaultValue="2. Choose your grade" />
                </p>
                <FloatingSelect
                  width={isDesktop ? undefined : '100%'}
                  value={grade}
                  options={[
                    {
                      value: 'Kindergarten',
                      displayName: t('Kindergarten', 'Kindergarten'),
                    },
                    { value: '1st', displayName: t('1st grade', '1st grade') },
                    { value: '2nd', displayName: t('2nd grade', '2nd grade') },
                    { value: '3rd', displayName: t('3rd grade', '3rd grade') },
                    { value: '4th', displayName: t('4th grade', '4th grade') },
                    { value: '5th', displayName: t('5th grade', '5th grade') },
                    { value: '6th', displayName: t('6th grade', '6th grade') },
                    { value: '7th', displayName: t('7th grade', '7th grade') },
                    { value: '8th', displayName: t('8th grade', '8th grade') },
                    { value: '9th', displayName: t('9th grade', '9th grade') },
                    { value: '10th', displayName: t('10th grade', '10th grade') },
                    { value: '11th', displayName: t('11th grade', '11th grade') },
                    { value: '12th', displayName: t('12th+ grade', '12th+ grade') },
                  ]}
                  onSelect={s => dispatchAndSync({ type: 'SET_GRADE', payload: s })}
                />
              </div>
            </div>
            <div>
              <p>
                <T
                  keyName="3. Additional criteria (optional)"
                  defaultValue="3. Additional criteria (optional)"
                />
              </p>

              <TopicInput
                prompt={currentPrompt}
                handleNewPrompt={(prompt: string) => setCurrentPrompt(prompt)}
                placeholderText={t(
                  'Include discussion questions... Make reflection questions... I want my students to be drawing... Make it relevant for my students living in...',
                  'Include discussion questions... Make reflection questions... I want my students to be drawing... Make it relevant for my students living in...',
                )}
              />
              {isUSUser && (
                <AlignToStandardsRow>
                  {selectedStandards.length > 0 && (
                    <SelectedStandardsBadges>
                      {selectedStandards.map(standard => (
                        <Label
                          key={standard.id}
                          style={{ marginBottom: 0, display: 'flex' }}
                        >
                          <IconWrapper
                            height="8px"
                            width="8px"
                            style={{ marginRight: 10, cursor: 'pointer' }}
                            onClick={() => removeStandard(standard.id)}
                          >
                            <CrossSvg />
                          </IconWrapper>
                          {standard.label}
                        </Label>
                      ))}
                    </SelectedStandardsBadges>
                  )}

                  <SingleSubjectStandardsButton
                    onClick={() => {
                      metrics.logEvent('CreatePage.standardSelectorOpened', {
                        subject: 'ela',
                        grade: grade,
                        prompt: currentPrompt,
                        schoolState: presetState,
                      });
                      setIsStandardsSelectorOpen({
                        isOpen: true,
                        canSwitchSubject: true,
                        subject: {
                          id: 'ela',
                          label: 'ela', //Default to ELA when we dont know the subject
                        },
                      });
                    }}
                    icon={
                      <IconWrapper height="14px" width="14px">
                        <AlignStandardsSvg />
                      </IconWrapper>
                    }
                  >
                    <T keyName="Align to standards" defaultValue="Align to standards" />
                  </SingleSubjectStandardsButton>
                </AlignToStandardsRow>
              )}
            </div>

            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: isLargerThan850 ? 'row' : 'column',
                gap: 10,
                alignItems: isLargerThan850 ? 'center' : 'start',
              }}
            >
              <span
                style={{
                  marginLeft: isLargerThan850 ? 0 : 10,
                  whiteSpace: 'nowrap',
                }}
              >
                <T keyName="4. Choose language" defaultValue="4. Choose language" />
              </span>

              <FloatingSelect
                width={isLargerThan850 ? '300px' : '100%'}
                value={languages[0]}
                options={supportedLanguages}
                onSelect={lang => {
                  dispatchAndSync({
                    type: 'SET_LANGUAGE',
                    payload: lang,
                  });
                }}
              />
            </div>

            <div
              style={{
                marginTop: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                margin: 'auto',
              }}
            >
              <GetLessonButton
                type="primary"
                width={isDesktop ? '280px' : '100%'}
                onClick={() => {
                  if (isGetLessonButtonDisabled) {
                    setHelpFillForm(true);
                    return;
                  }
                  handleGetLesson();
                }}
                loading={isGeneratingLesson}
                $disabled={isGetLessonButtonDisabled}
                icon={
                  <IconWrapper height="30px" width="30px">
                    <CuripodAIStarsSvg />
                  </IconWrapper>
                }
              >
                <T keyName="Generate Lesson" defaultValue="Generate Lesson" />
              </GetLessonButton>
              {isGetLessonButtonDisabled && helpFillForm && (
                <FormErrorText>{formErrorText}</FormErrorText>
              )}
            </div>
          </FormContainer>
        </SubPageLayout>
      )}
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
    </>
  );
};

const SingleSubjectStandardsButton = styled(Button)`
  background-color: ${theme.colors.paleBeige};
  color: ${theme.colors.black};
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }
`;

const Label = styled.p`
  font-size: 14px;
  padding: 2px 10px;
  border-radius: 5px;
  background: #cfede2;
  color: #177655;
  font-weight: 800;
  width: fit-content;
  margin-bottom: 5px;
  white-space: nowrap;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;
const FormErrorText = styled.p`
  color: ${theme.colors.red};
  font-size: 14px;
  margin: 0;
  margin-top: 10px;
  text-align: center;
  background-color: ${theme.colors.veryLightRed};
  border-radius: 10px;
  padding: 10px;
`;
const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;
const AlignToStandardsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 14px;
  gap: 8px;
`;

const SelectedStandardsBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
`;

const GetLessonButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: auto;

  svg {
    height: 25px;
    width: 25px;
  }

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      cursor: not-allowed;
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const UploadFileInput = ({
  onFileUploaded,
}: {
  onFileUploaded: (uploadedFile: UploadedFile) => void;
}) => {
  const { t } = useTranslate();
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const [isFileFormatErrorVisible, setIsFileFormatErrorVisible] = useState(false);
  const tenantId = useRouterMatch().tenantId;
  const {
    fileInfo: presentationFileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingPresentation,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingPresentation,
    reset: resetPresentationUpload,
  } = useUploadPresentation();

  const {
    fileInfo: googleDriveFileInfo,
    upload: uploadGoogleDrive,
    isUploading: isUploadingGoogleDrive,
    isSuccess: uploadGoogleDriveSuccess,
    isError: isErrorUploadingGoogleDrive,
    reset: resetGoogleDriveUpload,
  } = useUploadGoogleDrive(tenantId);

  const metrics = useMetrics();
  const { isReady: googleDriveIsReady, isError: googleDriveIsError } = useGoogleDrive(
    tenantId,
  );
  const isGoogleDriveFlagEnabled = useFlag('google_drive_integration');

  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);
  const [fileSizeZero, setFileSizeZero] = useState(false);
  const [presentation, setPresentation] = useState<File | undefined>(undefined);
  const [googleDriveFile, setGoogleDriveFile] = useState<CallbackDoc | undefined>(
    undefined,
  );
  const isDesktop = useBreakPoint(700);
  const handleLocalFileSelected = useCallback(
    (file: File) => {
      if (file.size === 0) {
        setFileSizeZero(true);
        return;
      }
      const fileSizeInMb = file.size * 1e-6;
      metrics.logEvent('Creator.Upload.Presentation.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setPresentation(file);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && presentationFileInfo && presentation) {
      onFileUploaded({
        name: presentation.name,
        fileType: presentationFileInfo.fileType,
        uploadedFileId: presentationFileInfo.filePath,
      });
      resetPresentationUpload();
    }
  }, [
    presentationFileInfo,
    uploadPresentationSuccess,
    resetPresentationUpload,
    onFileUploaded,
    presentation,
  ]);

  const handleGoogleDriveFileSelected = useCallback(
    (file: CallbackDoc) => {
      const fileSizeInMb = file.sizeBytes * 1e-6;
      metrics.logEvent('Creator.Upload.GoogleDrive.Generator.Selected', {
        fileSize: fileSizeInMb,
        fileType: file.type,
      });
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        setGoogleDriveFile(file);
        uploadGoogleDrive({ fileId: file.id, preferredMimeType: 'PPTX' });
      } else {
        setFileSizeExceeded(true);
      }
    },
    [metrics, uploadGoogleDrive],
  );
  const handleClearUploadedFile = () => {
    // Reset local file
    resetPresentationUpload();
    setPresentation(undefined);
    // Reset google drive file
    resetGoogleDriveUpload();
    setGoogleDriveFile(undefined);
    // Reset form input
    dispatchAndSync({
      type: 'SET_UPLOADED_FILE',
      payload: undefined,
    });
  };

  const { showFilePicker: showGoogleDrivePicker } = useGoogleDrivePicker(
    handleGoogleDriveFileSelected,
    tenantId,
    'PRESENTATIONS',
  );

  // Add ref for file input
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modified showLocalFilePicker function
  const showLocalFilePicker = useCallback(() => {
    if (!fileInputRef.current) {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.id = 'fileInput';
      fileInput.name = 'myFile';
      fileInput.multiple = false;
      fileInput.accept = isIos ? '*' : '.pptx, .key, .ppt, .pdf';
      fileInput.style.display = 'none';

      fileInput.onchange = () => {
        const supportedFormats = ['.pptx', '.key', '.ppt', '.pdf'];

        if (fileInput.files && fileInput.files.length > 0) {
          const fileName = fileInput.files[0].name;
          const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
          const isSupportedFormat = supportedFormats.includes(
            fileExtension.toLowerCase(),
          );
          if (!isSupportedFormat) {
            setIsFileFormatErrorVisible(true);
            return;
          }
          handleLocalFileSelected(fileInput.files[0]);
        }
      };

      fileInput.onclick = () => {
        fileInput.value = '';
      };

      fileInputRef.current = fileInput;
      document.body.appendChild(fileInput);
    }

    fileInputRef.current.click();
  }, [handleLocalFileSelected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fileInputRef.current) {
        document.body.removeChild(fileInputRef.current);
        fileInputRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (uploadGoogleDriveSuccess && googleDriveFileInfo && googleDriveFile) {
      onFileUploaded({
        name: googleDriveFile.name,
        fileType: googleDriveFileInfo.fileType,
        uploadedFileId: googleDriveFileInfo.filePath,
      });
      resetGoogleDriveUpload();
    }
  }, [
    googleDriveFileInfo,
    uploadGoogleDriveSuccess,
    resetGoogleDriveUpload,
    onFileUploaded,
    googleDriveFile,
  ]);

  // Joint status variables for both upload methods
  const uploadStatus = useMemo(() => {
    const isOngoing = isUploadingPresentation || isUploadingGoogleDrive;
    const isError = isErrorUploadingPresentation || isErrorUploadingGoogleDrive;
    const isSuccess = uploadPresentationSuccess || uploadGoogleDriveSuccess;
    const file = uploadedFile;
    const isStarted = !!file || isOngoing || isError || isSuccess;
    return {
      isOngoing,
      isError,
      isSuccess,
      isStarted,
      file,
    };
  }, [
    isUploadingPresentation,
    isUploadingGoogleDrive,
    isErrorUploadingPresentation,
    isErrorUploadingGoogleDrive,
    uploadPresentationSuccess,
    uploadGoogleDriveSuccess,
    uploadedFile,
  ]);

  const handleDropdownSelect = (option: string) => {
    if (option === 'local') {
      metrics.logEvent('Creator.Upload.Presentation.Generator.Clicked');
      showLocalFilePicker();
    }
    if (option === 'googleDrive') {
      metrics.logEvent('Creator.Upload.GoogleDrive.Generator.Clicked');
      showGoogleDrivePicker();
    }
  };

  return (
    <>
      <BaseModal
        visible={isFileFormatErrorVisible}
        onCancel={() => setIsFileFormatErrorVisible(false)}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            <>
              <ErrorMessage>{'Unsupported file format'}</ErrorMessage>

              <ErrorMessage style={{ fontSize: '18px' }}>
                <T
                  keyName="Please upload a file with .pptx, .key, .ppt, or .pdf format."
                  defaultValue="Please upload a file with .pptx, .key, .ppt, or .pdf format."
                />
              </ErrorMessage>
            </>
          </ErrorMessage>
          <Button
            styles={{ marginTop: 20 }}
            width="120px"
            onClick={() => setIsFileFormatErrorVisible(false)}
          >
            <T keyName="Ok" defaultValue="Ok" />
          </Button>
        </div>
      </BaseModal>

      {!uploadStatus.isStarted && (
        <FloatingSelect
          width={'100%'}
          value={undefined}
          placeholder={t('Select Presentation', 'Select Presentation')}
          options={[
            {
              value: 'local',
              displayName: t('Upload (.pdf, .pptx, .key)', 'Upload (.pdf, .pptx, .key)'),
              icon: <UploadsSvg />,
            },
            {
              value: 'googleDrive',
              displayName: isGoogleDriveFlagEnabled
                ? t('Google Drive', 'Google Drive')
                : t('Google Drive (Soon)', 'Google Drive (Soon)'),
              icon: !isGoogleDriveFlagEnabled ? (
                <GoogleDriveSvg />
              ) : googleDriveIsError ? (
                <ExclamationMarkSvg />
              ) : !googleDriveIsReady ? (
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{
                        fontSize: 20,
                        color: theme.colors.orange,
                        marginRight: 10,
                      }}
                      spin
                    />
                  }
                />
              ) : (
                <GoogleDriveSvg />
              ),
              disabled: !isGoogleDriveFlagEnabled || !googleDriveIsReady,
            },
          ]}
          onSelect={s => handleDropdownSelect(s)}
        />
      )}
      {uploadStatus.isOngoing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: `${theme.colors.white}`,
            borderRadius: 5,
            padding: '9px 15px',
            position: 'relative',
            boxShadow: theme.effects.floatingCreatorElementShadow,
            margin: isDesktop ? '0' : '10px 0',
          }}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 20,
                  color: theme.colors.orange,
                  marginRight: 10,
                }}
                spin
              />
            }
          />{' '}
          <T keyName="Uploading..." defaultValue="Uploading..." />
        </div>
      )}
      {uploadStatus.file && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: isDesktop ? 200 : '100%',
            background: `${theme.colors.paleBeige}`,
            borderRadius: 5,
            padding: '9px 15px',
            position: 'relative',
            boxShadow: theme.effects.floatingCreatorElementShadow,
            margin: isDesktop ? '0' : '10px 0',
          }}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <IconWrapper height="12px" width="12px" style={{ marginRight: 5 }}>
            <FolderSvg />
          </IconWrapper>
          <p
            style={{
              whiteSpace: 'nowrap',
              maxWidth: isDesktop ? 200 : '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: 0,
              marginLeft: 5,
            }}
          >
            {uploadStatus.file.name}
          </p>{' '}
          <IconWrapper
            height="8px"
            width="8px"
            style={{
              marginBottom: 10,
              boxShadow: theme.effects.magicFloatingShadow,
              borderRadius: 50,
              padding: 5,
              background: theme.colors.greyLight,
              position: 'absolute',
              right: '-8px',
              top: '-8px',
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleClearUploadedFile();
            }}
          >
            <CrossSvg />
          </IconWrapper>
        </div>
      )}
      {fileSizeExceeded && (
        <div style={{ display: 'inline-block' }}>
          <T keyName="The file was too large" defaultValue="The file was too large" />
        </div>
      )}
      {fileSizeZero && (
        <div style={{ display: 'inline-block' }}>
          <T keyName="The file was empty" defaultValue="The file was empty" />
        </div>
      )}
      {uploadStatus.isError && (
        <div>
          <T keyName="Failed to upload file" defaultValue="Failed to upload file" />
        </div>
      )}
    </>
  );
};

const FormContainer = styled.div`
  padding-top: 20px;
  margin: 0px auto;
  width: 100%;
  max-width: 900px;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (max-width: 700px) {
    padding-top: 0px;
  }
`;
